import { createContext, useContext, useEffect, useState } from "react";

import ConfigService from "@/services/configService";
import NotificationsService from "@/services/notificationsService";
import UsersService from "@/services/users";
import io from "socket.io-client";
import { toast } from "react-toastify";
import ChatService from "@/services/ChatService";
import { useQuery, useQueryClient } from "@tanstack/react-query";

const socket = io(import.meta.env.VITE_REACT_API_URL);

interface LayoutProps {
  fullLoading: boolean;
  setFullLoading: (value: boolean) => void;
  getConfig: any;
  errorGlobal: boolean;
  setErrorGlobal: (value: boolean) => void;
  userLogged: any;
  setUserLogged: (value: boolean) => void;
  getAllUser: any;
  setGetAllUser: (value: boolean) => void;
  notifications: any;
  setNotifications: (value: any) => void;
  chats: any;
  setChats: (value: any) => void;
  excelDashboard: any;
  setExcelDashboard: (value: any) => void;
  refreshNotifications: any;
  setRefreshNotifications: (value: any) => void;
  menuOpen: any;
  setMenuOpen: (value: any) => void;
  getMe: (value: any) => void;
  getChats: () => void;
  openChats: any[];
  setOpenChats: (value: any[]) => void;
  dataNotificacoes: any;
  setSearchChatsRecentes: (value: string) => void;
  searchChatsRecentes: any;
  openIAChat: boolean;
  setOpenIAChat: (value: boolean) => void;
}

const layoutContext = createContext({} as LayoutProps);

export const useLayout = (): LayoutProps => {
  return useContext(layoutContext);
};

function useProvideLayout(): LayoutProps {
  const [fullLoading, setFullLoading] = useState(true);
  const [errorGlobal, setErrorGlobal] = useState(false);
  const [userLogged, setUserLogged] = useState<any>({});
  const [getConfig, setGetConfig] = useState<any>({});
  const [getAllUser, setGetAllUser] = useState<any>({});
  const [initMe, setInitMe] = useState(false);
  const [notifications, setNotifications] = useState<any>([]);
  const [refreshNotifications, setRefreshNotifications] = useState<any>(false);
  const [excelDashboard, setExcelDashboard] = useState<any>([]);
  const [menuOpen, setMenuOpen] = useState(false);
  const [chats, setChats] = useState<any[]>([]);
  const [openChats, setOpenChats] = useState<any[]>([]);
  const [searchChatsRecentes, setSearchChatsRecentes] = useState<any>('');
  const { token } = JSON.parse(localStorage.getItem("token") || "{}");
  const queryClient = useQueryClient();
  const [openIAChat, setOpenIAChat] = useState<boolean>(false);

  const {
    isPending,
    data: dataNotificacoes,
    refetch,
  } = useQuery({
    queryKey: [`dataNotificacoes`, token],
    queryFn: async () => await NotificationsService.get(),
    enabled: !!token,
    retry: 3,
  });

  useEffect(() => {
    if (!userLogged.data?.id) {
      getMe();
    }
  }, [userLogged]);

  useEffect(() => {
    if (!isPending && !!dataNotificacoes?.data?.data) {
      setNotifications(dataNotificacoes?.data?.data || []);
    }
  }, [dataNotificacoes, isPending]);

  useEffect(() => {
    if (initMe) {
      const notificationHandler = (data: any) => {
        setNotifications((prevNotifications: any) => [
          data,
          ...prevNotifications,
        ]);
        refetch();
        queryClient.invalidateQueries({ queryKey: ["comentarios-nota"] });
        queryClient.invalidateQueries({ queryKey: ["comentarios-tarefa"] });
      };

      socket.on(`notification:${userLogged.data?.id}`, notificationHandler);
      return () => {
        socket.off(`notification:${userLogged.data?.id}`, notificationHandler);
      };

    }
  }, [initMe, userLogged.data?.id,]);

  useEffect(() => {
    if (initMe) {
      const chatUpdateHandler = async () => {
        await getChats();

        const newMessages = chats.some(
          (chat: any) =>
            chat.unread_count > 0 &&
            chat.lastMessage.sender_id !== userLogged.data?.id
        );

        if (newMessages) {
          const sound = new Audio("/notification-chat.mp3");
          sound.play();
        }
      };

      socket.on(
        `notification:chat:atualizarlistagemdechats:${userLogged.data?.id}`,
        chatUpdateHandler
      );

      return () => {
        socket.off(
          `notification:chat:atualizarlistagemdechats:${userLogged.data?.id}`,
          chatUpdateHandler
        );
      };
    }
  }, [initMe, userLogged.data?.id, chats]);

  async function getChats() {
    try {
      const res = await ChatService.listarConversasRecentes(searchChatsRecentes);

      if (res?.data?.success) {
        setChats(res?.data?.data || []);
      } else {
        console.error("Erro ao listar conversas recentes:", res.message);
      }
    } catch (error) {
      console.error("Erro ao listar conversas recentes:", error);
    }
  }

  async function getMe() {
    try {
      const { token } = JSON.parse(localStorage.getItem("token") || "{}");
      if (token) {
        const responseMe = await UsersService.me();
        if (responseMe.status === 200) {
          setUserLogged(responseMe.data);
          // getNotifications();
          refetch();
          await getChats();
          setInitMe(true);
        } else {
          toast.error("Não foi possível buscar informações do usuário");
          setErrorGlobal(true);
        }
      }
    } catch (error) {
      toast.error("Não foi possível buscar informações do usuário");
      setErrorGlobal(true);
    } finally {
      setFullLoading(false);
    }
  }

  useEffect(() => {
    if (userLogged.data?.id && !getConfig.ufs) {
      getConfigs();
    }
  }, [userLogged, getConfig]);

  async function getConfigs() {
    try {
      const { token } = JSON.parse(localStorage.getItem("token") || "{}");
      if (token) {
        const responseme = await ConfigService.configs();
        if (responseme.status === 200) {
          setGetConfig(responseme.data);
        } else {
          toast.error("Não foi possível buscar configurações do APP");
        }
      }
    } catch (error) {
      toast.error("Não foi possível buscar configurações do APP");
    }
  }

  return {
    fullLoading,
    setFullLoading,
    getConfig,
    errorGlobal,
    setErrorGlobal,
    userLogged,
    setUserLogged,
    getAllUser,
    setGetAllUser,
    notifications,
    setNotifications,
    excelDashboard,
    setExcelDashboard,
    refreshNotifications,
    setRefreshNotifications,
    menuOpen,
    setMenuOpen,
    getMe,
    chats,
    setChats,
    getChats,
    openChats,
    setOpenChats,
    dataNotificacoes,
    searchChatsRecentes,
    setSearchChatsRecentes,
    openIAChat,
    setOpenIAChat,
  };
}

const ProvideLayout: any = ({ children }: any) => {
  const layout: LayoutProps = useProvideLayout();
  return (
    <layoutContext.Provider value={layout}>{children}</layoutContext.Provider>
  );
};

export default ProvideLayout;
